/**
 * Shared parsing for dosage/package peptide variant names (e.g. "10mg - 2 Pack").
 * Used by PDP client selector and server-side price resolution.
 */

/**
 * Extract the dose in mg from a variant name (e.g. "10mg - 1 Pack" → 10, "2.5mg" → 2.5).
 * Treats the first dose token as the per-vial dose. Returns null when no dose is present.
 * Used by the calculator buy-bridge to match the user's chosen vial to a Saleor variant.
 */
export function parseVariantDoseMg(name: string): number | null {
	const match = name.match(/([\d.,]+)\s*(mg|ug|µg|μg|mcg)\b/i);
	if (!match) return null;
	const amount = Number.parseFloat(match[1]!.replace(",", "."));
	if (!Number.isFinite(amount)) return null;
	return match[2]!.toLowerCase() === "mg" ? amount : amount / 1_000;
}

export function parsePackageCountMatch(packageName: string): number | null {
	const match = packageName.match(
		/(\d+)\s*(?:Pack(?:ung)?|Vials?|Stück|Fläschchen|Pieces?|Unidad(?:es)?|Pi[eè]ces?|Flacons?|Pezz[oi]|Fial[ae]|Stuks?|Flesjes?|Unidades?|Frascos?|Sztuk[ai]?|Szt\.?)/i,
	);
	if (!match) return null;
	const count = Number.parseInt(match[1]!, 10);
	return Number.isSafeInteger(count) && count > 0 ? count : null;
}

export function parsePackageCount(packageName: string): number {
	return parsePackageCountMatch(packageName) ?? 1;
}

export function parseVariantName(name: string): { dosage: string; packageName: string } | null {
	const parts = name.split(" - ");
	if (parts.length < 2) return null;

	const left = parts[0]!.trim();
	const right = parts.slice(1).join(" - ").trim();

	const dosageMatch = left.match(/(\d+(?:[.,]\d+)?\s*(?:mg|ml)(?:\s*\/\s*\d+(?:[.,]\d+)?\s*(?:mg|ml))*)/i);
	const dosage = dosageMatch ? dosageMatch[1]!.replace(/\s+/g, " ").trim() : null;

	return {
		dosage: dosage ?? left,
		packageName: right,
	};
}
